import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { HelpCircleIcon, AlertCircleIcon } from 'lucide-react';

import { currentUserApi } from 'api';
import LoadingSpinner from 'components/common/loaders/LoadingSpinner';
import { ComboBoxField, ComboBoxFieldProps } from '@epoch-ui/components/Form';
import { Tooltip, TooltipTrigger, TooltipContent } from '@epoch-ui/components/Tooltip';

type EventTeamFieldProps = Omit<ComboBoxFieldProps, 'children' | 'triggerContent'>;

const Label = (): React.ReactNode => {
  return (
    <>
      Team&nbsp;
      <Tooltip>
        <TooltipTrigger asChild>
          <button type="button" aria-label="Team selection help" className="tw-m-0  tw-p-0">
            <HelpCircleIcon size={13} />
          </button>
        </TooltipTrigger>
        <TooltipContent className="tw-max-w-80">
          <p>
            Teams are groups within your company that organize events, such as an ERG or function.
            Selecting a team helps employees search for events and groups data for reporting.
          </p>
          <a
            target="_blank"
            rel="noreferrer"
            className="tw-text-primary-foreground tw-underline hover:tw-text-primary-foreground"
            href="https://help.epochapp.com/articles/what-is-a-team"
          >
            Learn more
          </a>
        </TooltipContent>
      </Tooltip>
    </>
  );
};

const EventTeamField: React.FC<EventTeamFieldProps> = (props) => {
  const { data, isLoading, error } = useQuery({
    queryKey: ['eventEditor', 'teams'],
    queryFn: () => currentUserApi.listTeams(),
    throwOnError: false
  });

  const getDisplayValue = React.useCallback(
    (teamId?: string) => {
      return teamId ? (
        <span className="tw-text-foreground">
          {data?.find((team) => String(team.id) === String(teamId))?.name}
        </span>
      ) : (
        'Select'
      );
    },
    [data]
  );

  return (
    <ComboBoxField
      label={<Label />}
      triggerContent={getDisplayValue}
      contentAvoidCollisions
      {...props}
    >
      <ComboBoxField.Command>
        <ComboBoxField.Input placeholder="Search" />
        <ComboBoxField.List className="tw-mt-3">
          {isLoading && <LoadingSpinner />}
          {error && (
            <div className="tw-flex tw-items-center tw-justify-center tw-gap-2 tw-text-destructive-foreground">
              <AlertCircleIcon size={16} />
              <div>Error while loading teams</div>
            </div>
          )}
          {!isLoading && !error && <ComboBoxField.Empty>No team found</ComboBoxField.Empty>}
          <ComboBoxField.Group>
            {data?.map((team) => (
              <ComboBoxField.Item key={team.id} value={team.id.toString()}>
                {team.name}
              </ComboBoxField.Item>
            ))}
          </ComboBoxField.Group>
        </ComboBoxField.List>
      </ComboBoxField.Command>
    </ComboBoxField>
  );
};

export { EventTeamField };
